import type { GraphMetadata } from '../content-editor/tiptap-graph-node-view';

export type GraphId = string | number;
export interface GraphVertex { id: GraphId; label: string; title?: string; icon?: string; [key: string]: unknown }
export interface GraphEdge { from: GraphId; to: GraphId; [key: string]: unknown }
export interface GraphExample { title?: string; desc?: string; hint?: string; items?: string[]; [key: string]: unknown }
export interface GraphData { nodes: GraphVertex[]; edges: GraphEdge[]; examples: Record<string, GraphExample> }
export const emptyGraphMetadata: GraphMetadata = { nodesJson: '[]', edgesJson: '[]', examplesJson: '{}' };
export const graphId = (id: GraphId) => String(id);
export const GRAPH_MIN_HEIGHT = 120;
export const GRAPH_MAX_HEIGHT = 2000;
export function readGraphHeight(value: unknown): string | null {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    const match = String(value).trim().match(/^(\d+(?:\.\d+)?|\.\d+)\s*(px|%|vh|vw|rem|em)?$/i);
    if (!match) return null;
    const height = Number(match[1]), unit = (match[2] || 'px').toLowerCase();
    if (!Number.isFinite(height) || height <= 0) return null;
    if (unit === 'px' && (height < GRAPH_MIN_HEIGHT || height > GRAPH_MAX_HEIGHT)) return null;
    return `${height}${unit}`;
}

/** Graph blocks have auto-height parents; percentages use their iframe viewport. */
export function graphHeightStyle(value: unknown): string | undefined {
    const height = readGraphHeight(value);
    return height?.endsWith('%') ? `${height.slice(0, -1)}vh` : height ?? undefined;
}
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const validId = (id: unknown): id is GraphId => (typeof id === 'string' && id.trim().length > 0
    && !['__proto__', 'constructor', 'prototype'].includes(id)) || (typeof id === 'number' && Number.isFinite(id));

function parsePart(text: string, name: string): unknown {
    try { return JSON.parse(text); }
    catch { throw new Error(`${name}: invalid JSON. Check quotes, commas and brackets. Your text has been retained.`); }
}

/** Keep entire records, including coordinates and properties not exposed by the builder. */
export function parseGraph(metadata: GraphMetadata): GraphData {
    const nodes = parsePart(metadata.nodesJson, 'Nodes');
    const edges = parsePart(metadata.edgesJson, 'Connections');
    const examples = parsePart(metadata.examplesJson, 'Examples');
    if (!Array.isArray(nodes)) throw new Error('Nodes must be a JSON array.');
    if (!Array.isArray(edges)) throw new Error('Connections must be a JSON array.');
    if (!object(examples)) throw new Error('Examples must be a JSON object keyed by node ID.');
    const ids = new Set<string>();
    for (const node of nodes) {
        if (!object(node) || !validId(node.id) || typeof node.label !== 'string') throw new Error('Each node needs a unique ID and a text label.');
        if (ids.has(graphId(node.id))) throw new Error(`Duplicate node ID: ${node.id}.`);
        ids.add(graphId(node.id));
        for (const key of ['title', 'icon']) {
            if (key in node && typeof node[key] !== 'string') throw new Error(`Node ${node.id}: ${key} must be text.`);
        }
    }
    const pairs = new Set<string>();
    for (const edge of edges) {
        if (!object(edge) || !validId(edge.from) || !validId(edge.to)) throw new Error('Each connection needs From and To node IDs.');
        const from = graphId(edge.from), to = graphId(edge.to);
        if (!ids.has(from) || !ids.has(to)) throw new Error(`Connection ${from} → ${to} refers to a missing node.`);
        if (from === to) throw new Error('A node cannot connect to itself.');
        const pair = JSON.stringify([from, to]);
        if (pairs.has(pair)) throw new Error(`Duplicate connection: ${from} → ${to}.`);
        pairs.add(pair);
    }
    for (const [id, example] of Object.entries(examples)) {
        if (!object(example)) throw new Error(`Examples for ${id} must be an object.`);
        for (const key of ['title', 'desc', 'hint']) {
            if (key in example && typeof example[key] !== 'string') throw new Error(`Examples for ${id}: ${key} must be text.`);
        }
        if ('items' in example && (!Array.isArray(example.items) || example.items.some(item => typeof item !== 'string'))) {
            throw new Error(`Examples for ${id}: items must be an array of text values.`);
        }
    }
    const graph = { nodes, edges, examples } as GraphData;
    graphLevels(graph); // Cycles cannot be displayed by the lesson's hierarchical renderer.
    return graph;
}

export function serializeGraph(graph: GraphData): GraphMetadata {
    return { nodesJson: JSON.stringify(graph.nodes, null, 2), edgesJson: JSON.stringify(graph.edges, null, 2), examplesJson: JSON.stringify(graph.examples, null, 2) };
}

export function validateGraphForSave(graph: GraphData): void {
    if (!graph.nodes.length) throw new Error('Add at least one node before saving the graph.');
    for (const node of graph.nodes) {
        if (!node.label.trim()) throw new Error(`Provide a label for node ${node.id}.`);
        const example = graphExample(graph, node.id);
        if (example && (!example.title?.trim() || !example.desc?.trim())) {
            throw new Error(`Click content for ${node.label} needs both a title and a description to display to learners.`);
        }
    }
}

export function graphExample(graph: GraphData, id: GraphId): GraphExample | undefined {
    return Object.prototype.hasOwnProperty.call(graph.examples, graphId(id)) ? graph.examples[graphId(id)] : undefined;
}

export function nextGraphNodeId(graph: GraphData): string {
    let index = 1;
    const ids = new Set([...graph.nodes.map(node => graphId(node.id)), ...Object.keys(graph.examples)]);
    while (ids.has(`node-${index}`)) index++;
    return `node-${index}`;
}

export function deleteGraphNode(graph: GraphData, id: GraphId): GraphData {
    const key = graphId(id);
    const examples = { ...graph.examples };
    delete examples[key];
    return { nodes: graph.nodes.filter(node => graphId(node.id) !== key),
        edges: graph.edges.filter(edge => graphId(edge.from) !== key && graphId(edge.to) !== key), examples };
}

/** Root-first order, matching the lesson renderer's automatic hierarchy. No x/y writes. */
export function graphLevels(graph: GraphData): string[][] {
    const children = new Map<string, string[]>();
    const incoming = new Map<string, number>();
    graph.nodes.forEach(node => { children.set(graphId(node.id), []); incoming.set(graphId(node.id), 0); });
    graph.edges.forEach(edge => {
        children.get(graphId(edge.from))!.push(graphId(edge.to));
        incoming.set(graphId(edge.to), incoming.get(graphId(edge.to))! + 1);
    });
    // Check for cycles without recursion, including disconnected components.
    const degree = new Map(incoming);
    const ready = [...degree.keys()].filter(id => degree.get(id) === 0);
    for (let i = 0; i < ready.length; i++) {
        children.get(ready[i])!.forEach(id => { degree.set(id, degree.get(id)! - 1); if (degree.get(id) === 0) ready.push(id); });
    }
    if (ready.length !== graph.nodes.length) throw new Error('Connections contain a cycle. Remove a connection so the graph can be positioned automatically.');
    const queue = [...incoming.keys()].filter(id => incoming.get(id) === 0).map(id => ({ id, depth: 0 }));
    const seen = new Set<string>();
    const levels: string[][] = [];
    for (let i = 0; i < queue.length; i++) {
        const { id, depth } = queue[i];
        if (seen.has(id)) continue;
        seen.add(id);
        (levels[depth] ??= []).push(id);
        children.get(id)!.forEach(child => queue.push({ id: child, depth: depth + 1 }));
    }
    return levels;
}

export function layoutGraph(graph: GraphData) {
    const levels = graphLevels(graph);
    const positions = new Map<string, { x: number; y: number; width: number }>();
    const widths = new Map(graph.nodes.map(node => [graphId(node.id), Math.max(140, ((node.title || node.label).length + (node.icon?.length ?? 0)) * 9 + 36)]));
    const gap = 44;
    const rowWidths = levels.map(row => row.reduce((sum, id) => sum + widths.get(id)!, 0) + Math.max(0, row.length - 1) * gap);
    const width = Math.max(500, ...rowWidths) + 70;
    levels.forEach((row, depth) => {
        let left = (width - rowWidths[depth]) / 2;
        row.forEach(id => { const w = widths.get(id)!; positions.set(id, { x: left + w / 2, y: 55 + depth * 130, width: w }); left += w + gap; });
    });
    return { positions, width, height: Math.max(260, levels.length * 130) };
}
